"use client"

/**
 * Autosave state indicator (Lane D).
 *
 * Pairs with useFormAutosave. Renders into the StickyActionBar's left
 * slot. Updates relative time every 30s while idle so "5 phút trước"
 * stays accurate without re-render thrash.
 */

import type { AutosaveState } from "@/hook/useFormAutosave"
import { t } from "@/languages/i18n"
import { CheckCircleFilled, CloseCircleFilled, LoadingOutlined } from "@ant-design/icons"
import { useEffect, useState } from "react"

interface AutosaveIndicatorProps {
  state: AutosaveState
  lastSavedAt: Date | null
  /** Optional retry handler shown when state is "error". */
  onRetry?: () => void
}

const formatRelative = (date: Date): string => {
  const seconds = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000))
  if (seconds < 30) return t("vài giây trước")
  if (seconds < 90) return "1" + t("phút trước")
  if (seconds < 3600) return `${Math.round(seconds / 60)} ${t("phút trước")}`
  if (seconds < 7200) return "1" + t("giờ trước")
  return `${Math.round(seconds / 3600)} ${t("giờ trước")}`
}

export function AutosaveIndicator({ state, lastSavedAt, onRetry }: AutosaveIndicatorProps) {
  // Tick every 30s while we have a lastSavedAt to keep relative time fresh.
  const [, force] = useState(0)
  useEffect(() => {
    if (!lastSavedAt) return
    const id = setInterval(() => force(n => n + 1), 30_000)
    return () => clearInterval(id)
  }, [lastSavedAt])

  if (state === "saving") {
    return (
      <span
        style={{
          fontSize: 12,
          color: "var(--admin-muted-fg)",
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        <LoadingOutlined spin style={{ fontSize: 12 }} aria-hidden />
        {t("Đang lưu...")}
      </span>
    )
  }

  if (state === "error") {
    return (
      <span
        style={{
          fontSize: 12,
          color: "var(--admin-danger)",
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        <CloseCircleFilled aria-hidden />
        {t("Không lưu được")} —{" "}
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            style={{
              background: "none",
              border: "none",
              color: "var(--admin-danger)",
              textDecoration: "underline",
              cursor: "pointer",
              padding: 0,
              fontSize: 12,
              fontFamily: "inherit",
            }}
          >
            {t("thử lại")}
          </button>
        )}
      </span>
    )
  }

  if (state === "saved" && lastSavedAt) {
    return (
      <span
        style={{
          fontSize: 12,
          color: "var(--admin-muted-fg)",
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        <CheckCircleFilled aria-hidden style={{ color: "var(--admin-accent)" }} />
        {t("Đã lưu nháp")} {formatRelative(lastSavedAt)}
      </span>
    )
  }

  if (state === "dirty") {
    return (
      <span style={{ fontSize: 12, color: "var(--admin-warning)" }}>
        {t("Có thay đổi chưa lưu")}
      </span>
    )
  }

  return (
    <span style={{ fontSize: 12, color: "var(--admin-muted-fg)" }}>{t("Chưa có thay đổi")}</span>
  )
}

export default AutosaveIndicator
